import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Bell, Lock, CreditCard, Building, Mail, Globe, Calendar, Monitor, Eye, EyeOff, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChangePassword } from '@mystudioclass/auth';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function SettingsPage() {
  const { user, studio, setUser, setStudio } = useAuth();
  const { changePassword: changePasswordApi, isLoading: passwordLoading } = useChangePassword();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');

  // Update active tab when URL param changes
  useEffect(() => {
    // FEATURE_DISABLED: removed 'notifications' and 'billing' from valid tabs - re-enable when ready
    if (tabFromUrl && ['profile', 'studio', 'scheduling', 'security'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);
  const [loading, setLoading] = useState(false);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Studio form state
  const [studioData, setStudioData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  // Scheduling settings state (includes display settings)
  const [schedulingSettings, setSchedulingSettings] = useState({
    videoToVideoGapEnabled: false,
    videoToVideoGap: 5,
    beforeLiveGapEnabled: true,
    beforeLiveGap: 15,
    afterLiveGapEnabled: true,
    afterLiveGap: 15,
    autoPlayInfoScreen: true,
    defaultMaxCapacity: 15
  });
  const [schedulingLoading, setSchedulingLoading] = useState(false);

  // Feature settings state
  const [featureSettings, setFeatureSettings] = useState({
    bookingEnabled: true
  });
  const [featureLoading, setFeatureLoading] = useState(false);

  // Initialize form data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || user.name?.split(' ')[0] || '',
        lastName: user.lastName || user.name?.split(' ')[1] || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Initialize and sync studio data when it changes (e.g., from WebSocket updates)
  useEffect(() => {
    if (studio) {
      setStudioData({
        name: studio.name || studio.businessName || '',
        address: studio.address || '',
        city: studio.city || '',
        state: studio.state || '',
        zipCode: studio.zipCode || '',
        phone: studio.phone || ''
      });
      // Sync scheduling settings from studio settings
      if (studio.settings) {
        setSchedulingSettings({
          videoToVideoGapEnabled: studio.settings.videoToVideoGapEnabled ?? false,
          videoToVideoGap: studio.settings.videoToVideoGap ?? 5,
          beforeLiveGapEnabled: studio.settings.beforeLiveGapEnabled ?? true,
          beforeLiveGap: studio.settings.beforeLiveGap ?? 15,
          afterLiveGapEnabled: studio.settings.afterLiveGapEnabled ?? true,
          afterLiveGap: studio.settings.afterLiveGap ?? 15,
          autoPlayInfoScreen: studio.settings.autoPlayInfoScreen ?? true,
          defaultMaxCapacity: studio.settings.defaultMaxCapacity ?? 15
        });
        // Initialize feature settings
        setFeatureSettings({
          bookingEnabled: studio.settings.bookingEnabled ?? true
        });
      }
    }
  }, [studio]);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'studio', label: 'Studio', icon: Building },
    { id: 'scheduling', label: 'Scheduling', icon: Calendar },
    // FEATURE_DISABLED: Notifications tab - re-enable when ready
    // { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Lock },
    // FEATURE_DISABLED: Billing tab - re-enable when ready
    // { id: 'billing', label: 'Billing', icon: CreditCard },
  ];

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Don't include email in profile update - use separate email update
      const profileWithoutEmail = { ...profileData };
      delete profileWithoutEmail.email;

      const response = await authAPI.updateCurrentUser(profileWithoutEmail);
      if (response.success) {
        // Update AuthContext with new user data
        setUser(response.data.user);
        if (response.data.studio) {
          setStudio(response.data.studio);
          localStorage.setItem('studio', JSON.stringify(response.data.studio));
        }
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));

        toast.success('Profile updated successfully!');
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStudio = async () => {
    setLoading(true);
    try {
      const response = await authAPI.updateStudio(studioData);
      if (response.success) {
        // Update AuthContext with new studio data
        setStudio(response.data.studio);
        localStorage.setItem('studio', JSON.stringify(response.data.studio));
        toast.success('Studio settings updated successfully!');
      } else {
        toast.error(response.message || 'Failed to update studio');
      }
    } catch (error) {
      console.error('Error updating studio:', error);
      toast.error(error.response?.data?.message || 'Failed to update studio. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!profileData.email || profileData.email === user?.email) {
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.updateEmail({ newEmail: profileData.email });
      if (response.success) {
        // Update AuthContext with new user data
        const updatedUser = { ...user, email: profileData.email };
        setUser(updatedUser);
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));

        toast.success('Email updated! Please use your new email address for future logins.');
      } else {
        toast.error(response.message || 'Failed to update email');
      }
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error(error.response?.data?.message || 'Failed to update email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedulingSettings = async () => {
    setSchedulingLoading(true);
    try {
      const response = await authAPI.updateStudioSettings(schedulingSettings);
      if (response.success) {
        // Update AuthContext by merging new settings with existing studio
        // Backend returns { settings: ... }, not the full studio object
        setStudio(prev => prev ? { ...prev, settings: response.data.settings } : prev);
        // Update localStorage (guard against "undefined" string)
        const studioStr = localStorage.getItem('studio');
        const storedStudio = (studioStr && studioStr !== 'undefined') ? JSON.parse(studioStr) : {};
        localStorage.setItem('studio', JSON.stringify({ ...storedStudio, settings: response.data.settings }));
        toast.success('Scheduling settings updated successfully!');
      } else {
        toast.error(response.message || 'Failed to update scheduling settings');
      }
    } catch (error) {
      console.error('Error updating scheduling settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update scheduling settings. Please try again.');
    } finally {
      setSchedulingLoading(false);
    }
  };

  const handleSaveFeatureSettings = async () => {
    setFeatureLoading(true);
    try {
      const response = await authAPI.updateStudioSettings(featureSettings);
      if (response.success) {
        // Update AuthContext by merging new settings with existing studio
        setStudio(prev => prev ? { ...prev, settings: response.data.settings } : prev);
        // Update localStorage
        const studioStr = localStorage.getItem('studio');
        const storedStudio = (studioStr && studioStr !== 'undefined') ? JSON.parse(studioStr) : {};
        localStorage.setItem('studio', JSON.stringify({ ...storedStudio, settings: response.data.settings }));
        toast.success('Feature settings saved successfully!');
      } else {
        toast.error(response.message || 'Failed to save feature settings');
      }
    } catch (error) {
      console.error('Error saving feature settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save feature settings. Please try again.');
    } finally {
      setFeatureLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      await changePasswordApi(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to update password. Check your current password.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">Settings</h1>
        <p className="text-[var(--text-secondary)] mt-2">Manage your account and preferences</p>
      </div>

      <div className="flex flex-col md:grid md:grid-cols-12 gap-4 md:gap-6">
        {/* Tabs - Horizontal scroll on mobile, sidebar on desktop */}
        <div className="md:col-span-3">
          <div className="card p-2 overflow-x-auto">
            <div className="flex md:flex-col gap-1 min-w-max md:min-w-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[var(--primary-12)] text-[var(--text-primary)] font-medium'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--highlight-10)]'
                    }`}
                  >
                    <Icon className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="text-sm md:text-base">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="md:col-span-9">
          {activeTab === 'profile' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Profile Settings</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">First Name</label>
                    <input
                      type="text"
                      className="input"
                      value={profileData.firstName}
                      onChange={(e) => handleProfileChange('firstName', e.target.value)}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="label">Last Name</label>
                    <input
                      type="text"
                      className="input"
                      value={profileData.lastName}
                      onChange={(e) => handleProfileChange('lastName', e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Email Address</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      className="input flex-1"
                      value={profileData.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      placeholder="you@example.com"
                    />
                    {profileData.email !== user?.email && (
                      <button
                        type="button"
                        onClick={handleUpdateEmail}
                        disabled={loading}
                        className="btn btn-primary whitespace-nowrap"
                      >
                        {loading ? 'Updating...' : 'Update Email'}
                      </button>
                    )}
                  </div>
                  {profileData.email !== user?.email && (
                    <p className="text-sm text-amber-600 mt-1">
                      Click "Update Email" to save. You'll use your new email address for future logins.
                    </p>
                  )}
                </div>

                <div>
                  <label className="label">Phone Number</label>
                  <input
                    type="tel"
                    className="input"
                    value={profileData.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      // Reset to original values
                      setProfileData({
                        firstName: user?.firstName || user?.name?.split(' ')[0] || '',
                        lastName: user?.lastName || user?.name?.split(' ')[1] || '',
                        email: user?.email || '',
                        phone: user?.phone || ''
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSaveProfile}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'studio' && (
            <div className="space-y-6">
              {/* Features Section */}
              <div className="card">
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Features</h2>
                <div className="space-y-6">
                  <div className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)]">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary-12)' }}>
                        <Calendar className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                      </div>
                      <div>
                        <h3 className="font-medium text-[var(--text-primary)]">Class Booking</h3>
                        <p className="text-sm text-[var(--text-secondary)]">Allow customers to book spots in your scheduled classes</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={featureSettings.bookingEnabled}
                        onChange={(e) => setFeatureSettings({ ...featureSettings, bookingEnabled: e.target.checked })}
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setFeatureSettings({
                          bookingEnabled: studio?.settings?.bookingEnabled ?? true
                        });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveFeatureSettings}
                      disabled={featureLoading}
                    >
                      {featureLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Studio Info Section */}
              <div className="card">
                <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Studio Information</h2>
                <div className="space-y-6">
                  <div>
                    <label className="label">Studio Name</label>
                    <input
                      type="text"
                      className="input"
                      value={studioData.name}
                      onChange={(e) => setStudioData({ ...studioData, name: e.target.value })}
                      placeholder="Your studio name"
                    />
                  </div>

                  <div>
                    <label className="label">Address</label>
                    <input
                      type="text"
                      className="input"
                      value={studioData.address}
                      onChange={(e) => setStudioData({ ...studioData, address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">City</label>
                      <input
                        type="text"
                        className="input"
                        value={studioData.city}
                        onChange={(e) => setStudioData({ ...studioData, city: e.target.value })}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="label">State</label>
                      <input
                        type="text"
                        className="input"
                        value={studioData.state}
                        onChange={(e) => setStudioData({ ...studioData, state: e.target.value })}
                        placeholder="State"
                        maxLength="2"
                      />
                    </div>
                    <div>
                      <label className="label">Zip Code</label>
                      <input
                        type="text"
                        className="input"
                        value={studioData.zipCode}
                        onChange={(e) => setStudioData({ ...studioData, zipCode: e.target.value })}
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Phone</label>
                    <input
                      type="tel"
                      className="input"
                      value={studioData.phone}
                      onChange={(e) => setStudioData({ ...studioData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        // Reset to original values
                        if (studio) {
                          setStudioData({
                            name: studio.name || studio.businessName || '',
                            address: studio.address || '',
                            city: studio.city || '',
                            state: studio.state || '',
                            zipCode: studio.zipCode || '',
                            phone: studio.phone || ''
                          });
                        }
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveStudio}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'scheduling' && (
            <div className="card">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-[var(--text-primary)]">Scheduling & Display</h2>
                <p className="text-sm text-[var(--text-secondary)]">Configure buffer times and display behavior</p>
              </div>

              {/* Buffer Settings Grid - responsive: 1 col on mobile, 3 cols on desktop */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* Video to Video Gap */}
                <div className="rounded-xl p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Gap Between Two Video Classes</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={schedulingSettings.videoToVideoGapEnabled}
                        onChange={(e) => setSchedulingSettings({ ...schedulingSettings, videoToVideoGapEnabled: e.target.checked })}
                      />
                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Another video class cannot START within this time after a video class ends.</p>
                  <select
                    className="input text-sm py-1.5 w-full"
                    value={schedulingSettings.videoToVideoGap}
                    onChange={(e) => setSchedulingSettings({ ...schedulingSettings, videoToVideoGap: parseInt(e.target.value) })}
                    disabled={!schedulingSettings.videoToVideoGapEnabled}
                    style={{ opacity: schedulingSettings.videoToVideoGapEnabled ? 1 : 0.4 }}
                  >
                    {[5, 10, 15, 20, 30, 45, 60].map(min => (
                      <option key={min} value={min}>{min} min</option>
                    ))}
                  </select>
                </div>

                {/* Before Live Gap */}
                <div className="rounded-xl p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Gap Before Live Class</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={schedulingSettings.beforeLiveGapEnabled}
                        onChange={(e) => setSchedulingSettings({ ...schedulingSettings, beforeLiveGapEnabled: e.target.checked })}
                      />
                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Video or live classes cannot END within this time before a live class begins.</p>
                  <select
                    className="input text-sm py-1.5 w-full"
                    value={schedulingSettings.beforeLiveGap}
                    onChange={(e) => setSchedulingSettings({ ...schedulingSettings, beforeLiveGap: parseInt(e.target.value) })}
                    disabled={!schedulingSettings.beforeLiveGapEnabled}
                    style={{ opacity: schedulingSettings.beforeLiveGapEnabled ? 1 : 0.4 }}
                  >
                    {[5, 10, 15, 20, 30, 45, 60].map(min => (
                      <option key={min} value={min}>{min} min</option>
                    ))}
                  </select>
                </div>

                {/* After Live Gap */}
                <div className="rounded-xl p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Gap After Live Class</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={schedulingSettings.afterLiveGapEnabled}
                        onChange={(e) => setSchedulingSettings({ ...schedulingSettings, afterLiveGapEnabled: e.target.checked })}
                      />
                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Video or live classes cannot START within this time after a live class ends.</p>
                  <select
                    className="input text-sm py-1.5 w-full"
                    value={schedulingSettings.afterLiveGap}
                    onChange={(e) => setSchedulingSettings({ ...schedulingSettings, afterLiveGap: parseInt(e.target.value) })}
                    disabled={!schedulingSettings.afterLiveGapEnabled}
                    style={{ opacity: schedulingSettings.afterLiveGapEnabled ? 1 : 0.4 }}
                  >
                    {[5, 10, 15, 20, 30, 45, 60].map(min => (
                      <option key={min} value={min}>{min} min</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Display Settings - Compact row */}
              <div className="border-t border-[var(--border-subtle)] pt-4">
                <div className="flex items-center justify-between rounded-xl p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center gap-3">
                    <Monitor className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                    <div>
                      <h3 className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Auto-Play Info Screen</h3>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Show studio info between scheduled videos</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={schedulingSettings.autoPlayInfoScreen}
                      onChange={(e) => setSchedulingSettings({ ...schedulingSettings, autoPlayInfoScreen: e.target.checked })}
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>

              {/* Max Capacity Setting */}
              <div className="border-t border-[var(--border-subtle)] pt-4">
                <div className="flex items-center justify-between rounded-xl p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                    <div>
                      <h3 className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>Default Max Attendance</h3>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Maximum attendees for new calendar events</p>
                    </div>
                  </div>
                  <select
                    className="input text-sm py-1.5 w-24"
                    value={schedulingSettings.defaultMaxCapacity}
                    onChange={(e) => setSchedulingSettings({ ...schedulingSettings, defaultMaxCapacity: parseInt(e.target.value) })}
                  >
                    {Array.from({ length: 50 }, (_, i) => i + 1).map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Save/Cancel Buttons */}
              <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-[var(--border-subtle)]">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    if (studio?.settings) {
                      setSchedulingSettings({
                        videoToVideoGapEnabled: studio.settings.videoToVideoGapEnabled ?? false,
                        videoToVideoGap: studio.settings.videoToVideoGap ?? 5,
                        beforeLiveGapEnabled: studio.settings.beforeLiveGapEnabled ?? true,
                        beforeLiveGap: studio.settings.beforeLiveGap ?? 15,
                        afterLiveGapEnabled: studio.settings.afterLiveGapEnabled ?? true,
                        afterLiveGap: studio.settings.afterLiveGap ?? 15,
                        autoPlayInfoScreen: studio.settings.autoPlayInfoScreen ?? true,
                        defaultMaxCapacity: studio.settings.defaultMaxCapacity ?? 15
                      });
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSchedulingSettings}
                  disabled={schedulingLoading}
                >
                  {schedulingLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Notification Preferences</h2>
              <div className="space-y-6">
                <div className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)]">
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)]">Email Notifications</h3>
                    <p className="text-sm text-[var(--text-secondary)]">Receive emails about your account activity</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" style={{ background: 'var(--highlight-20)' }}></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)]">
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)]">Class Reminders</h3>
                    <p className="text-sm text-[var(--text-secondary)]">Get notified before scheduled classes</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" style={{ background: 'var(--highlight-20)' }}></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)]">
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)]">Marketing Updates</h3>
                    <p className="text-sm text-[var(--text-secondary)]">Receive updates about new features and tips</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" style={{ background: 'var(--highlight-20)' }}></div>
                  </label>
                </div>

                <div className="flex items-center justify-between py-4">
                  <div>
                    <h3 className="font-medium text-[var(--text-primary)]">Payment Notifications</h3>
                    <p className="text-sm text-[var(--text-secondary)]">Get notified about billing and payments</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" style={{ background: 'var(--highlight-20)' }}></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Security Settings</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-[var(--text-primary)] mb-4">Change Password</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="label">Current Password</label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          className="input pr-10"
                          placeholder="Enter current password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="label">New Password</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          className="input pr-10"
                          placeholder="Enter new password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="label">Confirm New Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="input pr-10"
                          placeholder="Confirm new password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={handleChangePassword}
                      disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    >
                      {passwordLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>

                <div className="pt-6 border-t border-[var(--border-subtle)]">
                  <h3 className="font-medium text-[var(--text-primary)] mb-4">Two-Factor Authentication</h3>
                  <p className="text-sm text-[var(--text-secondary)] mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <button className="btn btn-outline">Enable 2FA</button>
                </div>

                <div className="pt-6 border-t border-[var(--border-subtle)]">
                  <h3 className="font-medium text-[var(--text-primary)] mb-4">Active Sessions</h3>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-elevated)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[var(--text-primary)]">Current Session</p>
                        <p className="text-sm text-[var(--text-secondary)]">Windows • Chrome • United States</p>
                      </div>
                      <span className="text-sm text-green-600 font-medium">Active Now</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="card">
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Billing & Payment</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-[var(--text-primary)] mb-4">Current Plan</h3>
                  <div className="p-6 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xl font-bold text-[var(--text-primary)]">{studio?.subscription_tier?.toUpperCase()} Plan</h4>
                        <p className="text-[var(--text-secondary)] mt-1">Billed monthly</p>
                      </div>
                      <button className="btn btn-outline">Change Plan</button>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-[var(--text-primary)] mb-4">Payment Method</h3>
                  <div className="p-4 rounded-lg flex items-center justify-between" style={{ background: 'var(--bg-elevated)' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-8 bg-primary-600 rounded flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-[var(--text-primary)]">•••• •••• •••• 4242</p>
                        <p className="text-sm text-[var(--text-secondary)]">Expires 12/25</p>
                      </div>
                    </div>
                    <button className="btn btn-secondary">Update</button>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-[var(--text-primary)] mb-4">Billing History</h3>
                  <div className="space-y-2">
                    {[
                      { date: 'Oct 1, 2024', amount: '$29.99', status: 'Paid' },
                      { date: 'Sep 1, 2024', amount: '$29.99', status: 'Paid' },
                      { date: 'Aug 1, 2024', amount: '$29.99', status: 'Paid' },
                    ].map((invoice, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--bg-elevated)' }}>
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">{invoice.date}</p>
                          <p className="text-sm text-[var(--text-secondary)]">{invoice.amount}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-green-600 font-medium">{invoice.status}</span>
                          <button className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Download</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
